import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { makeStyles } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { yupResolver } from '@hookform/resolvers/yup';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { submitLogin, submitGroupChoice, submitRoleChoice, resetLoginState } from 'app/auth/store/loginSlice';
import { logoutUserSilent } from 'app/auth/store/userSlice';
import { Link } from 'react-router-dom';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import ChooseGroupPanel from './ChooseGroupPanel';
import ChooseRolePanel from './ChooseRolePanel';

const useStyles = makeStyles(theme => ({
	root: {
		background: `linear-gradient(to right, ${theme.palette.primary.dark} 0%, ${darken(theme.palette.primary.dark, 0.5)} 100%)`,
		color: theme.palette.primary.contrastText
	},
	leftSection: {}
}));

const defaultValues = {
	email: '',
	password: ''
};

const NO_GROUP = {
	title: 'No group is assigned to your account',
	content: 'Your account has no group assigned. Please contact an administrator.'
};

const NO_ROLE = {
	title: 'No role is assigned to your account',
	content: 'Your account has no role assigned. Please contact an administrator.'
};

const backendMessage = item => {
	if (!item) return '';
	if (typeof item === 'string') return item;
	const msg = item.directMessage === true ? item.message : item.message || item.key;
	return msg ? String(msg) : '';
};

function Login() {
	const classes = useStyles();
	const { t } = useTranslation(['auth', 'validation']);
	const dispatch = useDispatch();
	const login = useSelector(({ auth }) => auth.login);
	const inProgress = !!login?.inProgress;
	const isChoosing = login?.nextStep === 'chooseGroup' || login?.nextStep === 'chooseRole';

	const schema = yup.object().shape({
		email: yup.string().email(t('validation:EMAIL_IS_NOT_VALID')).required(t('validation:EMAIL_IS_REQUIRED')),
		password: yup.string().required(t('validation:PASSWORD_REQUIRED'))
	});

	const { control, setValue, formState, handleSubmit, reset, trigger, setError } = useForm({
		mode: 'onChange',
		defaultValues,
		resolver: yupResolver(schema)
	});

	const { errors } = formState;

	const [showPassword, setShowPassword] = useState(false);

	useEffect(() => {
		setValue('email', '', { shouldDirty: true, shouldValidate: true });
		setValue('password', '', { shouldDirty: true, shouldValidate: true });
	}, [reset, setValue, trigger]);

	useEffect(() => {
		if (Array.isArray(login?.errors)) {
			login.errors.forEach(error => {
				setError(error.type, {
					type: 'manual',
					message: error.message
				});
			});
			return;
		}

		const candidate = login?.errors && typeof login.errors === 'object' ? login.errors : null;
		if (!candidate) return;

		if (candidate.properties && typeof candidate.properties === 'object') {
			Object.entries(candidate.properties).forEach(([field, raw]) => {
				const items = Array.isArray(raw) ? raw : [raw];
				const message = items.map(backendMessage).filter(Boolean).join(' | ');
				if (!message) return;

				const safeField = Object.keys(defaultValues).includes(field) ? field : 'email';
				setError(safeField, { type: 'server', message });
			});
			return;
		}

		const message = backendMessage(candidate);
		if (message) setError('email', { type: 'server', message });
	}, [login?.errors, setError]);

	const messageConfig = useMemo(() => {
		if (login?.nextStep === 'noGroup') return NO_GROUP;
		if (login?.nextStep === 'noRole') return NO_ROLE;
		return null;
	}, [login?.nextStep]);

	const groups = login?.meta?.groups ?? login?.client?.userGroups ?? [];
	const roles = login?.meta?.roles ?? login?.client?.roles ?? [];
	const hardDisabled = !!login?.success || !!login?.isLoggedIn;

	function onSubmit(model) {
		dispatch(submitLogin(model));
	}

	const handleSelectGroup = group => {
		const groupId = group?.id ?? group?.userGroupId ?? group;
		dispatch(submitGroupChoice(groupId));
	};

	const handleSelectRole = role => {
		dispatch(submitRoleChoice(role));
	};

	const backToLogin = async () => {
		try {
			await dispatch(logoutUserSilent());
		} catch (e) {}

		dispatch(resetLoginState());
		reset(defaultValues);
		setValue('email', '', { shouldDirty: true, shouldValidate: true });
		setValue('password', '', { shouldDirty: true, shouldValidate: true });
	};

	return (
		<div className={clsx(classes.root, 'flex flex-col flex-auto items-center justify-center flex-shrink-0 p-16 md:p-24')}>
			<motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} className="flex min-w-320 max-w-400 md:max-w-3xl rounded-20 shadow-2xl overflow-hidden">
				<Card className={clsx(classes.leftSection, 'flex flex-col w-full max-w-sm items-center justify-center shadow-0')} square>
					<CardContent className="flex flex-col items-center justify-center w-full py-24 max-w-320">
						<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.2 } }}>
							<div className="flex items-center pt-8 pb-32">
								<img className="w-full h-auto" src="assets/images/logos/nboss_logo_big.png" alt="nBoss Admin" />
							</div>
						</motion.div>

						<div className="w-full">
							{messageConfig ? (
								<div className="w-full flex flex-col gap-12 text-center">
									<Typography className="text-18 font-semibold">{messageConfig.title}</Typography>
									<Typography className="text-13 opacity-80">{messageConfig.content}</Typography>
									<Button type="button" variant="contained" color="primary" className="w-full mt-8" disabled={inProgress} onClick={backToLogin}>
										Back to login
									</Button>
								</div>
							) : isChoosing ? (
								<div className="w-full flex flex-col gap-12">
									{login?.nextStep === 'chooseRole' && <ChooseRolePanel roles={roles} onSelect={handleSelectRole} disabled={inProgress} />}
									{login?.nextStep === 'chooseGroup' && <ChooseGroupPanel groups={groups} onSelect={handleSelectGroup} disabled={inProgress} />}

									<Button type="button" variant="contained" color="primary" className="w-full" disabled={inProgress || hardDisabled} onClick={backToLogin}>
										Cancel
									</Button>
								</div>
							) : (
								<form className="flex flex-col justify-center w-full" onSubmit={handleSubmit(onSubmit)}>
									<Controller
										name="email"
										control={control}
										render={({ field }) => (
											<TextField
												{...field}
												autoComplete="username"
												className="mb-16"
												type="text"
												error={!!errors.email}
												helperText={errors?.email?.message}
												label={t('EMAIL')}
												InputProps={{
													className: 'pr-4',
													endAdornment: (
														<InputAdornment position="end" className="ml-4">
															<Icon className="text-20 mx-12" color="action">
																email
															</Icon>
														</InputAdornment>
													)
												}}
												variant="outlined"
											/>
										)}
									/>

									<Controller
										name="password"
										control={control}
										render={({ field }) => (
											<TextField
												{...field}
												autoComplete="current-password"
												className="mb-16"
												label={t('PASSWORD')}
												type="password"
												error={!!errors.password}
												helperText={errors?.password?.message}
												variant="outlined"
												InputProps={{
													className: 'pr-4',
													type: showPassword ? 'text' : 'password',
													endAdornment: (
														<InputAdornment position="end" className="ml-4">
															<IconButton onClick={() => setShowPassword(!showPassword)}>
																<Icon className="text-20" color="action">
																	{showPassword ? 'visibility' : 'visibility_off'}
																</Icon>
															</IconButton>
														</InputAdornment>
													)
												}}
												required
											/>
										)}
									/>

									<div className="w-full flex flex-col gap-8 mt-8">
										<Button type="submit" variant="contained" color="primary" className="w-full" aria-label="LOG IN" value="legacy">
											{t('LOGIN')}
										</Button>
									</div>
								</form>
							)}
						</div>
					</CardContent>

					{!messageConfig && !isChoosing && (
						<div className="flex flex-col items-center justify-center pb-24">
							<div>
								<span className="font-normal">{t('LOST_YOUR_PASSWORD')} </span>
								<Link color="secondary" className="font-semibold" to="/password-request">
									{t('REQUEST_A_NEW_PASSWORD')}
								</Link>
							</div>
						</div>
					)}
				</Card>
			</motion.div>
		</div>
	);
}

export default Login;
